import {
  SortBy,
  AreaPageListingResponse,
  MapPageListingResponse,
  MapPageBoundarySearchResponse,
  MapPageBoundarySearchV2Response,
  MapPageFilter,
  MapPinResponse,
  LatLonAsArray,
} from '@zoocasa/go-search';
import fetchAreaPageListings from './area/fetcher';
import { DefaultFilterStrategy, LegacyFilterStrategy, AreaFilterStrategy } from './area/area-filter-strategy';
import { getGoSearchHost, isServerSide } from 'utils/host-config';
import { SearchByAreaFilterType } from './area/types';
import { Bounds } from 'components/search-es/types';
import { Precision } from 'components/search-es/geo_helper';
import { GeoFilterStrategy, DefaultFilterStrategy as GeoDefaultFilterStrategy, LegacyFilterStrategy as GeoLegacyFilterStrategy } from './map/map-filter-strategy';
import { BoundaryPaginationMode, GeoSearchFilter } from './map/types';
import { MAP_PAGE_CONSTANTS } from 'constants/pagination';
import { fetchClusters, fetchListings, fetchListingsV2 } from './map/fetcher';
import { fetchPins } from './map/pin-fetcher';
import { ThemeName } from 'themes';
import { fetchSearchPredictionsV1 } from './predictions/fetcher';
import type { SearchPredictionsApiResponse, SearchPredictionsParams } from './predictions/types';

//#region Types

/**
 * SearchApi interface type that represents the api methods available for the search service
 */
export type SearchApiType = {

  /**
   * Fetches all listings for an area page using the given parameters.
   *
   * @param country The country to search within
   * @param province The province to search within
   * @param city The city to search within
   * @param neighbourhood The neighbourhood to search within
   * @param filter The filter to apply to the search
   * @param tenant The theme name is passed for limiting listings shown
   * @param sortBy The sort order of the results
   * @param page The page number to return
   * @param size  An optional number of results to return. Will use default value if not provided
   */
  searchByArea(country: string, province: string, city: string, neighbourhood: string, filter: SearchByAreaFilterType, tenant: ThemeName, sortBy: SortBy, page: number, size?: number): Promise<AreaPageListingResponse>;

  /**
   * Searches for clusters within a boundary using the given precision and filter. Results are sorted by the given sortBy parameter.
   *
   * @param bounds The bounding box to search within
   * @param precision The precision of the search. See [Geohash](https://en.wikipedia.org/wiki/Geohash#Digits_and_precision_in_km) for more details.
   * @param filter The filter to apply to the search
   * @param sortBy The sort order of the results
   * @param signal An optional AbortSignal to cancel the request
   * @param size  An optional number of results to return. Will use default value if not provided
   */
  searchClustersInBoundary(bounds: Bounds, precision: Precision, filter: GeoSearchFilter, sortBy: SortBy, signal?: AbortSignal, size?: number): Promise<MapPageListingResponse>;

  /**
   * Searches for clusters within an area using the given precision and filter. Results are sorted by the given sortBy parameter.
   *
   * @param areaSlug The area slug to search within
   * @param precision The precision of the search. See [Geohash](https://en.wikipedia.org/wiki/Geohash#Digits_and_precision_in_km) for more details.
   * @param filter The filter to apply to the search
   * @param sortBy The sort order of the results
   * @param signal An optional AbortSignal to cancel the request
   * @param size  An optional number of results to return. Will use default value if not provided
   */
  searchClustersWithinArea(areaSlug: string, precision: Precision, filter: GeoSearchFilter, sortBy: SortBy, signal?: AbortSignal, size?: number, bounds?: Bounds): Promise<MapPageListingResponse>;

  /**
   * Searches for listings within a boundary using the given precision, filter, page, and sortBy parameters.
   *
   * @param bounds The bounding box to search within
   * @param precision The precision of the search. See [Geohash](https://en.wikipedia.org/wiki/Geohash#Digits_and_precision_in_km) for more details.
   * @param page Interpreted per {@link BoundaryPaginationMode}
   * @param filter The filter to apply to the search
   * @param sortBy The sort order of the results
   * @param signal An optional AbortSignal to cancel the request
   * @param size  An optional  number of results to return. Will use default value if not provided
   * @param paginationMode Defaults to `cursor`
   */
  searchListingsInBoundary(bounds: Bounds | null, precision: Precision, page: string, filter: GeoSearchFilter, sortBy: SortBy, signal?: AbortSignal, size?: number, areaSlug?: string, paginationMode?: BoundaryPaginationMode): Promise<MapPageBoundarySearchResponse>;

  /**
   * Searches for building-grouped pins within a boundary. Each pin represents either
   * a standalone listing or a building with multiple units.
   *
   * @param bounds The bounding box to search within
   * @param precision The geohash precision for the search
   * @param filter The filter to apply to the search
   * @param sortBy The sort order of the results
   * @param signal An optional AbortSignal to cancel the request
   * @param limit Maximum number of pins to return
   * @param includeListings When true, server includes `listings[]` previews per pin
   * @param offset Offset-based pagination. Default 0. Server caps `offset + limit` at 5000.
   */
  searchPinsInBoundary(bounds: Bounds, precision: Precision, filter: GeoSearchFilter, sortBy: SortBy, signal?: AbortSignal, limit?: number, includeListings?: boolean, offset?: number): Promise<MapPinResponse>;

  /**
   * Building-grouped pins for hybrid / area map views. Same viewport as {@link searchPinsInBoundary}
   * (`latLonAsArray`) plus `areaSlug` on the filter — parity with `/api/map/clusters` (listings_v2 keyword
   * should-clauses on neighbourhood / sub_division / province_or_state slugs). Hybrid pages should pass
   * {@link getListingsV2MapAreaSlug} so values match index keywords (e.g. `milton` not `milton-on`).
   */
  searchPinsWithinArea(areaSlug: string, bounds: Bounds, precision: Precision, filter: GeoSearchFilter, sortBy: SortBy, signal?: AbortSignal, limit?: number, includeListings?: boolean, offset?: number): Promise<MapPinResponse>;

  /**
   * Searches for boundary listings using the v2 endpoint (returns BoundaryListing[]).
   * Used for building cluster expansion where the full AreaPageListing shape is not needed.
   */
  searchListingsInBoundaryV2(bounds: Bounds | null, precision: Precision, filter: GeoSearchFilter, sortBy: SortBy, signal?: AbortSignal, size?: number): Promise<MapPageBoundarySearchV2Response>;

  /**
   * Searches for location predictions including listings, schools, buildings, military bases, and geographic locations.
   *
   * @param params Search parameters including query string and optional filters
   * @returns Promise resolving to search predictions response or null if the request fails
   */
  searchPredictions(params: SearchPredictionsParams): Promise<SearchPredictionsApiResponse | null>;
};
//#endregion

export class SearchApi implements SearchApiType {
  private host: string;
  private areaFilterStrategy: AreaFilterStrategy;
  private geoFilterStrategy: GeoFilterStrategy;

  private constructor(host: string, filterStrategy: AreaFilterStrategy, geoFilterStrategy: GeoFilterStrategy) {
    this.host = host;
    this.areaFilterStrategy = filterStrategy;
    this.geoFilterStrategy = geoFilterStrategy;
  }

  /**
   * Creates a new SearchApi instance with the given host and filter strategy. Legacy search filter does not include farm, land, or commercial properties.
   *
   * @param host The host to use for the search api. Defaults to the result of getGoSearchHost(isServerSide())
   * @param isLegacy Whether to use the legacy search filter. Legacy search filter does not include farm, land, or commercial properties. Defaults to false
   */
  static create(host: string = getGoSearchHost(isServerSide()), isLegacy: boolean = false): SearchApi {
    const filterStrategy = isLegacy ? LegacyFilterStrategy : DefaultFilterStrategy;
    const geoFilterStrategy = isLegacy ? GeoLegacyFilterStrategy : GeoDefaultFilterStrategy;
    return new SearchApi(host, filterStrategy, geoFilterStrategy);
  }

  /**
   * Replaces the geohash-snapped corners from the filter strategy with the exact
   * viewport coordinates. Geohash corners are expanded outward to whole cells by
   * Elasticsearch, so results (and cluster centroids) can fall outside the visible
   * map; exact corners keep the search box identical to what the map displays.
   */
  private static applyExactBounds(apiFilter: MapPageFilter, bounds: Bounds): void {
    apiFilter.latLonAsGeohash = undefined;
    apiFilter.latLonAsArray = LatLonAsArray.fromPartial({
      topLeft: [bounds.north, bounds.west],
      bottomRight: [bounds.south, bounds.east],
    });
  }

  /**
   * Fetches all listings for an area page using the given parameters.
   *
   * @param country The country to search within
   * @param province The province to search within
   * @param city The city to search within
   * @param neighbourhood The neighbourhood to search within
   * @param filter The filter to apply to the search
   * @param sortBy The sort order of the results. Defaults to date descending if not provided
   * @param page The page number to return. Defaults to 1 if not provided
   * @param size The number of results to return. Defaults to AREA_PAGE_CONSTANTS.DEFAULT_PAGE_SIZE if not provided
   * @param tenant Determine the Tenant of the host in order to serve the correct data
   */
  async searchByArea(country: string, province: string, city: string, neighbourhood: string, filter: SearchByAreaFilterType, tenant: ThemeName, sortBy: SortBy = SortBy.DateDesc, page: number = 1, size?: number): Promise<AreaPageListingResponse> {
    const apiFilter = this.areaFilterStrategy(filter);
    return await fetchAreaPageListings(country, province, city, neighbourhood, sortBy, apiFilter, page, size, this.host, tenant);
  }

  /**
   * Searches for clusters within a boundary using the given precision and filter. Results are sorted by the given sortBy parameter and defaults to date descending if not provided.
   *
   * @param bounds The bounding box to search within
   * @param precision The precision of the search. See [Geohash](https://en.wikipedia.org/wiki/Geohash#Digits_and_precision_in_km) for more details.
   * @param filter The filter to apply to the search
   * @param sortBy The sort order of the results. Defaults to date descending if not provided
   * @param signal An optional AbortSignal to cancel the request
   * @param size The number of results to return. Defaults to MAP_PAGE_CONSTANTS.DEFAULT_CLUSTER_DATA_SIZE if not provided
   */
  async searchClustersInBoundary(bounds: Bounds, precision: Precision, filter: GeoSearchFilter, sortBy: SortBy = SortBy.DateDesc, signal?: AbortSignal, size: number = MAP_PAGE_CONSTANTS.DEFAULT_CLUSTER_DATA_SIZE): Promise<MapPageListingResponse> {
    const apiFilter = this.geoFilterStrategy(bounds, precision, filter);
    SearchApi.applyExactBounds(apiFilter, bounds);
    return await fetchClusters(apiFilter, sortBy, signal, size, this.host);
  }

  /**
   * Searches for clusters within an area using the given precision and filter. Results are sorted by the given sortBy parameter and defaults to date descending if not provided.
   *
   * @param areaSlug The area slug to search within
   * @param precision The precision of the search. See [Geohash](https://en.wikipedia.org/wiki/Geohash#Digits_and_precision_in_km) for more details.
   * @param filter The filter to apply to the search
   * @param sortBy The sort order of the results. Defaults to date descending if not provided
   * @param signal An optional AbortSignal to cancel the request
   * @param size The number of results to return. Defaults to MAP_PAGE_CONSTANTS.DEFAULT_CLUSTER_DATA_SIZE if not provided
   * @param bounds The bounding box to search within
   */
  async searchClustersWithinArea(areaSlug: string, precision: Precision, filter: GeoSearchFilter, sortBy: SortBy = SortBy.DateDesc, signal?: AbortSignal, size: number = MAP_PAGE_CONSTANTS.DEFAULT_CLUSTER_DATA_SIZE, bounds?: Bounds): Promise<MapPageListingResponse> {
    const apiFilter = this.geoFilterStrategy(bounds, precision, filter, areaSlug);
    if (bounds) {
      SearchApi.applyExactBounds(apiFilter, bounds);
    }
    return await fetchClusters(apiFilter, sortBy, signal, size, this.host);
  }

  async searchListingsInBoundary(bounds: Bounds | null, precision: Precision, page: string, filter: GeoSearchFilter, sortBy: SortBy = SortBy.DateDesc, signal?: AbortSignal, size: number = MAP_PAGE_CONSTANTS.DEFAULT_CLUSTER_DATA_SIZE, areaSlug?: string, paginationMode: BoundaryPaginationMode = 'cursor'): Promise<MapPageBoundarySearchResponse> {
    const apiFilter = this.geoFilterStrategy(bounds, precision, filter, areaSlug);
    if (bounds) {
      SearchApi.applyExactBounds(apiFilter, bounds);
    }
    return await fetchListings(apiFilter, sortBy, page, signal, size, this.host, paginationMode);
  }

  async searchPinsInBoundary(bounds: Bounds, precision: Precision, filter: GeoSearchFilter, sortBy: SortBy = SortBy.DateDesc, signal?: AbortSignal, limit: number = 200, includeListings: boolean = false, offset: number = 0): Promise<MapPinResponse> {
    const apiFilter = this.geoFilterStrategy(bounds, precision, filter);
    SearchApi.applyExactBounds(apiFilter, bounds);
    return await fetchPins(apiFilter, sortBy, signal, limit, offset, this.host, includeListings);
  }

  async searchPinsWithinArea(areaSlug: string, bounds: Bounds, precision: Precision, filter: GeoSearchFilter, sortBy: SortBy = SortBy.DateDesc, signal?: AbortSignal, limit: number = 200, includeListings: boolean = false, offset: number = 0): Promise<MapPinResponse> {
    const apiFilter = this.geoFilterStrategy(bounds, precision, filter, areaSlug);
    SearchApi.applyExactBounds(apiFilter, bounds);
    return await fetchPins(apiFilter, sortBy, signal, limit, offset, this.host, includeListings);
  }

  async searchListingsInBoundaryV2(bounds: Bounds | null, precision: Precision, filter: GeoSearchFilter, sortBy: SortBy = SortBy.DateDesc, signal?: AbortSignal, size: number = MAP_PAGE_CONSTANTS.DEFAULT_CLUSTER_DATA_SIZE): Promise<MapPageBoundarySearchV2Response> {
    const apiFilter = this.geoFilterStrategy(bounds, precision, filter);
    return await fetchListingsV2(apiFilter, sortBy, signal, size, this.host);
  }

  /**
   * Searches for location predictions including listings, schools, military bases, and geographic locations.
   *
   * This method provides autocomplete-style search suggestions from the go-search service.
   * Results include various types of locations that can be used for search input suggestions.
   *
   * @param params Search parameters including query string and optional filters
   * @returns Promise resolving to search predictions response or null if the request fails
   */
  async searchPredictions(params: SearchPredictionsParams): Promise<SearchPredictionsApiResponse | null> {
    return await fetchSearchPredictionsV1(params, this.host);
  }
}

/**
 * Creates a new SearchApi instance with the given host and filter
 * strategy. Legacy search filter does not include farm, land, or commercial properties.
 */
export const createSearchApi = SearchApi.create;
